import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, CircularProgress, IconButton, useTheme,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { tokens } from '../../../../theme';
import { useSessionExpiry } from '../../../../utils/useSessionExpiry';
import v2Api from '../../../../utils/v2Api';
import getCookies from '../../../../utils/getCookies';
import { TestSessionProvider, useTestSession } from './TestSessionContext';
import ReviewScreen from './components/ReviewScreen';
import PdfRenderer from './renderers/PdfRenderer';
import QuestionUIRenderer from './renderers/QuestionUIRenderer';
import dayjs from 'dayjs';

const TestSessionInner = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { id: courseUuid, courseTestId } = useParams();
    const navigate = useNavigate();

    const currentUser = getCookies("_auth_state");
    const userRole = currentUser ? currentUser.split(',')[2]?.split(':')[1]?.split('"')[1] : null;
    const isAdmin = userRole === 'admin';

    const {
        session, test, courseTest, answers,
        currentSectionIndex, setCurrentSectionIndex,
        currentQuestionIndex, setCurrentQuestionIndex,
        isDirty, save, debouncedSave, submit,
        saving, submitting, submitError, saveError, setSaveError,
        hydrate, setSession,
    } = useTestSession();

    const { renewSession } = useSessionExpiry();

    const handleRenewSession = async () => {
        try {
            await renewSession();
            setSaveError(null);
            save();
        } catch { /* renewal failed — banner stays visible */ }
    };

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reviewOpen, setReviewOpen] = useState(false);

    // Admin proxy / preview mode: read from URL params
    const urlParams = new URLSearchParams(window.location.search);
    const proxyStudentParam = urlParams.get('student');
    const isPreview = urlParams.get('preview') === 'true';
    // Retakeable tests: ?retake=true asks the server for a fresh attempt (new random
    // subset) when the latest attempt is finished. Safe to linger — the server only
    // starts a new attempt if the latest is already submitted/graded.
    const retakeParam = urlParams.get('retake') === 'true';

    // Kill outer .content scroll via CSS class — auto-removed when component unmounts
    useEffect(() => {
        const style = document.createElement('style');
        style.id = 'v2-test-session-no-outer-scroll';
        style.textContent = 'main.content { overflow: hidden !important; }';
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    // Init session
    const [needsSessionConfirm, setNeedsSessionConfirm] = useState(false);
    const [previewView, setPreviewView] = useState('student'); // 'student' | 'teacher'

    const loadPreview = async (view) => {
        try {
            setLoading(true);
            setError(null);
            const params = view === 'teacher' ? { view: 'teacher' } : {};
            const res = await v2Api.get(`/course-tests/${courseTestId}/preview`, params);
            const data = res.data.data;
            hydrate({
                session: { id: null, status: 'preview' },
                test: data.test,
                course_test: data.course_test,
                saved_answers: [],
            });
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load test');
        } finally {
            setLoading(false);
        }
    };

    const handlePreviewViewChange = (view) => {
        setPreviewView(view);
        loadPreview(view);
    };

    const loadSession = async (forceCreate = false) => {
        try {
            setLoading(true);
            setError(null);
            setNeedsSessionConfirm(false);

            if (isPreview) {
                await loadPreview(previewView);
                return;
            } else {
                const params = proxyStudentParam ? { student_id: proxyStudentParam } : {};
                if (forceCreate && proxyStudentParam) params.create_session = true;
                if (retakeParam) params.retake = true;
                const res = await v2Api.get(`/course-tests/${courseTestId}/test-taking`, params);
                const data = res.data.data;

                // Redirect if already submitted (unless admin proxying — they can edit)
                if (!isAdmin && (data.session.status === 'submitted' || data.session.status === 'graded')) {
                    navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/result/${data.session.id}`, { replace: true });
                    return;
                }

                hydrate(data);
                // Drop ?retake from the URL once consumed, so a refresh/bookmark/back
                // can't silently spawn another attempt later.
                if (retakeParam) navigate(window.location.pathname, { replace: true });
            }
        } catch (err) {
            if (err.response?.status === 404 && isAdmin && proxyStudentParam) {
                setNeedsSessionConfirm(true);
            } else {
                setError(err.response?.data?.message || 'Failed to load test');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadSession();
    }, [courseTestId, courseUuid]); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-save on section navigation
    const handleSectionNavigate = useCallback((idx) => {
        if (isDirty) save();
        setCurrentSectionIndex(idx);
        setCurrentQuestionIndex(0);
    }, [isDirty, save, setCurrentSectionIndex, setCurrentQuestionIndex]);

    // Unsaved changes dialog state
    const [exitDialogOpen, setExitDialogOpen] = useState(false);
    const [pendingExitAction, setPendingExitAction] = useState(null);

    // Browser tab close / refresh — native dialog (can't customize). Skip in preview mode.
    useEffect(() => {
        if (isPreview) return;
        const handler = (e) => {
            if (isDirty) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty, isPreview]);

    // Browser back button — intercept and show custom dialog. Skip in preview mode.
    useEffect(() => {
        if (!isDirty || isPreview) return;
        const handlePopState = () => {
            window.history.pushState(null, '', window.location.href);
            setPendingExitAction(() => () => window.history.go(-2));
            setExitDialogOpen(true);
        };
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [isDirty]);

    const handleSaveAndExit = async () => {
        await save();
        setExitDialogOpen(false);
        if (pendingExitAction) pendingExitAction();
    };

    const handleExitWithoutSaving = () => {
        setExitDialogOpen(false);
        if (pendingExitAction) pendingExitAction();
    };

    // Submit handler — gradingMode: undefined (student default), 'none', 'immediate', 'delayed'
    const handleSubmit = async (gradingMode) => {
        try {
            await submit(gradingMode);
            if (isAdmin) {
                navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/grading`, { replace: true });
            } else {
                navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/result/${session.id}`, { replace: true });
            }
        } catch { /* submitError is set in context */ }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (needsSessionConfirm) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>This student has not started this test yet.</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Creating a session will mark the test as in-progress for this student.</Typography>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button variant="outlined" onClick={() => navigate(-1)}>Go Back</Button>
                    <Button variant="contained" onClick={() => loadSession(true)}>Start Session</Button>
                </Box>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error" variant="h6">{error}</Typography>
                <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
            </Box>
        );
    }

    if (!test || !session) return null;

    const sections = test.v2_test_sections || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', mx: 2 }}>
            {/* Header */}
            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: colors.grey[700], display: 'flex', alignItems: 'center', gap: 2 }}>
                {isAdmin && (proxyStudentParam || isPreview) && (
                    <IconButton size="small" onClick={() => navigate(`/classrooms/${courseUuid}/test-v2/${courseTestId}/grading`)}>
                        <ArrowBackIcon />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight={600} noWrap sx={{ flex: 1 }}>
                    {courseTest?.title || test?.title}
                    {isPreview && (
                        <Typography component="span" variant="body2" ml={1.5}
                            sx={{ color: colors.blueAccent[400], display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}
                        >
                            <VisibilityIcon sx={{ fontSize: 16 }} /> Preview
                        </Typography>
                    )}
                    {!isPreview && courseTest?.due_date && (() => {
                        const now = dayjs();
                        const due = dayjs(courseTest.due_date);
                        if (due.isBefore(now)) return <Typography component="span" variant="body2" color="error" ml={1.5}>Past due</Typography>;
                        const diffMin = due.diff(now, 'minute');
                        const days = Math.floor(diffMin / 1440);
                        const hours = Math.floor((diffMin % 1440) / 60);
                        const mins = diffMin % 60;
                        const parts = [];
                        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
                        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
                        if (mins > 0) parts.push(`${mins} min`);
                        const label = parts.join(' ') || '< 1 min';
                        const urgent = diffMin < 120;
                        return <Typography component="span" variant="body2" color={urgent ? 'warning.main' : 'text.secondary'} ml={1.5}>Due in {label}</Typography>;
                    })()}
                </Typography>

                {!isPreview && <>
                    {saving && <Typography variant="caption" color="text.secondary">Saving...</Typography>}

                    {saveError === 'auth' && !saving && (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 16 }} /> Session expired
                        </Typography>
                    )}
                    {saveError === 'network' && !saving && (
                        <Typography variant="caption" color="error" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ErrorOutlineIcon sx={{ fontSize: 16 }} /> Save failed — retrying...
                        </Typography>
                    )}

                    {isDirty ? (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={saving ? <CircularProgress size={16} /> : <SaveOutlinedIcon />}
                            onClick={save}
                            disabled={saving}
                            sx={{ borderColor: colors.blueAccent[600], color: colors.blueAccent[600], ':hover': { borderColor: colors.blueAccent[400], bgcolor: `${colors.blueAccent[600]}10` } }}
                        >
                            Save
                        </Button>
                    ) : (
                        session && answers.size > 0 && !saving && !saveError && (
                            <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <CheckCircleOutlineIcon sx={{ fontSize: 16 }} /> Saved
                            </Typography>
                        )
                    )}
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<RateReviewOutlinedIcon />}
                        onClick={() => { if (isDirty) save(); setReviewOpen(true); }}
                        sx={{ bgcolor: colors.blueAccent[600], color: 'white', ':hover': { bgcolor: colors.blueAccent[700] } }}
                    >
                        Review & Submit
                    </Button>
                </>}
                {isPreview && (
                    <ToggleButtonGroup
                        value={previewView}
                        exclusive
                        onChange={(_, v) => { if (v) handlePreviewViewChange(v); }}
                        size="small"
                    >
                        <ToggleButton value="student" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Student</ToggleButton>
                        <ToggleButton value="teacher" sx={{ textTransform: 'none', fontSize: 12, px: 1.5 }}>Teacher</ToggleButton>
                    </ToggleButtonGroup>
                )}
            </Box>

            {saveError === 'auth' && (
                <Box sx={{ px: 2, py: 1, bgcolor: 'warning.dark', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 18 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>
                        Your login session has timed out. Don't worry — your answers are safe. Please sign in again to continue.
                    </Typography>
                </Box>
            )}

            {submitError && (
                <Typography color="error" variant="body2" sx={{ px: 2, py: 0.5 }}>{submitError}</Typography>
            )}

            {/* Renderer */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {test?.use_question_ui ? <QuestionUIRenderer /> : <PdfRenderer />}
            </Box>

            <ReviewScreen
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onConfirmSubmit={handleSubmit}
                sections={sections}
                isAdmin={isAdmin && !!proxyStudentParam}
            />

            {/* Unsaved changes dialog */}
            <Dialog open={exitDialogOpen} onClose={() => setExitDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Unsaved Changes
                </DialogTitle>
                <DialogContent>
                    <Typography>You have unsaved answers. Would you like to save before leaving?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExitDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleExitWithoutSaving} color="error">Leave without saving</Button>
                    <Button onClick={handleSaveAndExit} variant="contained"
                        sx={{ bgcolor: colors.blueAccent[600], ':hover': { bgcolor: colors.blueAccent[700] } }}
                    >
                        Save & Leave
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// Wrapper that provides context
const TestSession = () => (
    <TestSessionProvider>
        <TestSessionInner />
    </TestSessionProvider>
);

export default TestSession;
